import { useEffect, useState } from 'react';
import useCountdownTimer from '../../custom-hooks/countdown-timer';
import './buttons.scss';

interface StandardButtonProps {
  buttonText: string;
  secondaryButtonText?: string;
  clickHandler: () => void;
  classNames?: string;
  holdDownMode?: boolean;
}

/**
 * A standard button component with optional classes.
 * @param {string} buttonText - The text to be displayed on the button.
 * @param {string} secondaryButtonText - (Optional) The text to be displayed below the buttonText
 * @param {any} clickHandler - The function to be called when the button is clicked.
 * @param {string} [classNames] - (Optional) Additional class names to apply to the button element.
 * @param {boolean} [holdDownMode] - (Optional) A boolean to enable hold down mode, which requires the button to be held down for 3 seconds before executing the clickHandler.
 *
 */
const StandardButton: React.FC<StandardButtonProps> = ({ buttonText, secondaryButtonText, clickHandler, classNames, holdDownMode }) => {
  const [holdTimeRemaining, toggleTimer, setHoldTimeRemaining] = useCountdownTimer(3000);
  const [holding, setHolding] = useState<boolean>(false);
  const [secondaryText, setSecondaryText] = useState<string>(secondaryButtonText || '');

  // added a useEffect hook to update the secondary text when holding is true
  useEffect(() => {
    if (holding) {
      setSecondaryText(`${Math.ceil(holdTimeRemaining / 1000)}`);
      if (holdTimeRemaining <= 0) {
        clickHandler();
        setHolding(false);
      }
    } else {
      setSecondaryText(secondaryButtonText || '');
    }
  }, [holding, holdTimeRemaining, secondaryButtonText]);
 
  /**
   * Handles the button click event and executes the clickHandler if enabled.
   */
  function handleClick(e: any): void {
    e.stopPropagation();
    if(holdDownMode || classNames?.includes('disabled')) {
      return;
    }
    clickHandler();
  }

  /**
   * Handles the button mouse down event and if in hold down mode sets the holding state to true and starts the timer.
   */
  function handleMouseDown(): void {
    if (classNames?.includes('disabled')) {
      return;
    }
    if (holdDownMode) {
      setHolding(true);
      toggleTimer(true);
    }
  }

  /**
   * Handles the button mouse up event and if in hold down mode sets the holding state to false and stops the timer.
   */
  function handleMouseUp(): void {
    if (holdDownMode) {
      setHolding(false);
      toggleTimer(false);
      setHoldTimeRemaining(3000);
    }
  }
  
  return (
    <div 
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
      onPointerLeave={handleMouseUp}
      className={`button ${classNames}`}>
      <div className={`primary-text${secondaryText ? ' has-secondary' : ''}`}>{buttonText}</div>
      {secondaryButtonText &&
        <div key={secondaryText} className={`secondary-text ${holding ? 'countdown' : ''}`}>{secondaryText}</div>
      }
    </div>
  )
}

export default StandardButton;